import * as React from 'react'

import {cn} from '@/lib/utils'

const Card = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & {'data-testid'?: string | undefined}
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <div
        ref={ref}
        className={cn('rounded-lg border bg-card text-card-foreground shadow-sm', className)}
        data-testid={dataTestId || 'Card'}
        {...props}
    />
))
Card.displayName = 'Card'

const CardHeader = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & {'data-testid'?: string | undefined}
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <div
        ref={ref}
        className={cn('flex flex-col space-y-1.5 p-6', className)}
        data-testid={dataTestId || 'CardHeader'}
        {...props}
    />
))
CardHeader.displayName = 'CardHeader'

interface CardTitleProps extends React.ComponentPropsWithoutRef<'h3'> {
    className?: string
    children?: React.ReactNode
    'data-testid': string | undefined
}

const CardTitle = React.forwardRef<HTMLParagraphElement, CardTitleProps>(
    ({className, children, 'data-testid': dataTestId, ...props}, ref) => (
        <h3
            ref={ref}
            className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
            data-testid={dataTestId || 'CardTitle'}
            {...props}
        >
            {children}
        </h3>
    )
)
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
    HTMLParagraphElement,
    React.HTMLAttributes<HTMLParagraphElement> & {'data-testid'?: string | undefined}
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <p
        ref={ref}
        className={cn('text-sm text-muted-foreground', className)}
        data-testid={dataTestId || 'CardDescription'}
        {...props}
    />
))
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & {'data-testid'?: string | undefined}
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <div ref={ref} className={className} data-testid={dataTestId || 'CardContent'} {...props} />
))
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & {'data-testid'?: string | undefined}
>(({className, 'data-testid': dataTestId, ...props}, ref) => (
    <div
        ref={ref}
        className={cn('flex items-center p-6 pt-0', className)}
        data-testid={dataTestId || 'CardFooter'}
        {...props}
    />
))
CardFooter.displayName = 'CardFooter'

export {Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent}
